import { useEffect, useState } from "react";
import { AgreeTerms } from "@/components/asv/AgreeTerms";
import { CartFields, missingFields } from "@/components/asv/CartFields";
import { useNavigate } from "react-router-dom";
import { X, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, rm, fmtDate, formatErr } from "@/lib/api";
import { useCart, usePlayer, viewerId } from "@/lib/asv";

export function CartDrawer({ open, onClose }) {
  const { cart, remove, clear } = useCart();
  const { player } = usePlayer();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [hours, setHours] = useState(6);
  const [answers, setAnswersRaw] = useState(() => { try { return JSON.parse(sessionStorage.getItem("asv_cart_answers") || "{}"); } catch { return {}; } });
  const setAnswers = (a) => { setAnswersRaw(a); sessionStorage.setItem("asv_cart_answers", JSON.stringify(a)); };
  useEffect(() => { api.get("/asv/config").then((r) => setHours(r.data.refund_hours ?? 6)).catch(() => {}); }, []);

  useEffect(() => {
    if (!open || !cart.items.length) { setQ(null); return; }
    const t = setTimeout(() => api.post("/asv/cart/quote", { vendor_slug: cart.vendor_slug, items: cart.items, answers }).then((r) => setQ(r.data)).catch((e) => toast.error(formatErr(e))), 300);
    return () => clearTimeout(t);
  }, [open, cart, answers, player]);
  const missing = missingFields(q?.fields, answers);

  const checkout = async () => {
    if (!player) { onClose(); nav("/signin?next=/?cart=1"); return; }
    setBusy(true);
    try {
      const r = await api.post("/asv/cart/checkout", { vendor_slug: cart.vendor_slug, items: cart.items, viewer: viewerId(), agree_terms: agree, answers });
      clear();
      sessionStorage.removeItem("asv_cart_answers");
      if (r.data.paid) nav(`/account?order=${r.data.order_id}`); else window.location.href = r.data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || formatErr(e));
      setQ((x) => x && d?.unavailable ? { ...x, unavailable: d.unavailable } : x);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const bad = (it) => q?.unavailable?.some((u) => u.pitch_id === it.pitch_id && u.date === it.date && u.slot_start === it.slot_start);
  return (
    <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
      <button className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close cart" data-testid="cart-overlay" />
      <aside className="asv-rise absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l asv-line" style={{ background: "var(--asv-bg)" }}>
        <div className="flex items-center justify-between border-b asv-line p-5">
          <div><p className="asv-display text-xl font-extrabold">Your booking</p>{cart.vendor_name && <p className="asv-muted text-sm">{cart.vendor_name}</p>}</div>
          <button onClick={onClose} className="asv-btn-ghost !p-2" data-testid="cart-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {!cart.items.length && <div className="asv-muted py-16 text-center text-sm"><ShoppingBag className="mx-auto mb-3 h-8 w-8" />Your cart is empty. Add slots from any venue.</div>}
          {cart.items.map((it) => {
            const line = q?.lines?.find((l) => l.pitch_id === it.pitch_id && l.date === it.date && l.slot_start === it.slot_start);
            return (
              <div key={`${it.pitch_id}${it.date}${it.slot_start}`} className={`asv-card flex items-center gap-3 p-4 ${bad(it) ? "border-red-500" : ""}`} data-testid="cart-item">
                <div className="min-w-0 flex-1"><p className="text-sm font-bold">{fmtDate(it.date)} · {it.slot_start}{it.slot_end ? `-${it.slot_end}` : ""}</p>
                  <p className="asv-muted truncate text-xs">{it.pitch_name}</p>{bad(it) && <p className="text-xs font-semibold text-red-500" data-testid="cart-item-unavailable">No longer available. Remove it to continue.</p>}</div>
                <div className="text-right"><span className="text-sm font-bold">{line ? rm(line.price_sen) : ""}</span>
                  {!!line?.extras_sen && <p className="asv-muted text-[11px]" data-testid="cart-item-extras">incl. {rm(line.extras_sen)} add-ons</p>}</div>
                <button onClick={() => remove(it)} className="asv-muted hover:text-red-500" data-testid="cart-item-remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          {!!cart.items.length && <CartFields fields={q?.fields} names={Object.fromEntries((q?.lines || []).map((l) => [l.pitch_id, l.pitch_name]))} vendor={cart.vendor_name} answers={answers} onChange={setAnswers} />}
        </div>
        {!!cart.items.length && (
          <div className="space-y-2 border-t asv-line p-5 text-sm">
            <div className="flex justify-between"><span className="asv-muted">Subtotal</span><span data-testid="cart-subtotal">{rm(q?.subtotal_sen || 0)}</span></div>
            {!!q?.discount_sen && <div className="flex justify-between asv-accent font-semibold"><span>{q.discount_label}</span><span data-testid="cart-discount">-{rm(q.discount_sen)}</span></div>}
            <div className="flex justify-between text-base font-extrabold"><span>Total</span><span data-testid="cart-total">{q ? rm(q.total_sen) : "…"}</span></div>
            {player && !!missing.length && <p className="text-xs font-semibold text-amber-500" data-testid="cart-missing-fields">Please fill in: {missing.join(", ")}</p>}
            {player && <div className="pt-2"><AgreeTerms checked={agree} onChange={setAgree} testid="cart-agree-terms" text="I agree to the" /></div>}
            {player && <p className="asv-muted text-[11px]" data-testid="cart-refund-note">Refunds are only available within {hours} hours after payment.</p>}
            <button className="asv-btn mt-3 w-full" disabled={busy || !q || q.unavailable?.length || (player && (!agree || missing.length))} onClick={checkout} data-testid="cart-checkout">{player ? "Pay once with CHIP" : "Sign in to check out"}</button>
            <button className="asv-muted w-full text-xs underline" onClick={clear} data-testid="cart-clear">Clear cart</button>
          </div>
        )}
      </aside>
    </div>
  );
}
